/* eslint-disable no-unused-vars */
import Amplify, { Auth, API, graphqlOperation } from 'aws-amplify';
import router from '@/router';
import * as queries from '../graphql/queries';
import * as mutations from '../graphql/mutations';
import { configuration } from './aws-exports';
import { log } from '@/constants.js';
import { withStatisticsDates } from './statisticsParams.js';

Amplify.configure(configuration);

// -------------------- AUTH --------------------------
// You can get the current config object
export const currentConfig = Auth.configure();

export const getJwtToken = async () => {
  let jwt;
  await Auth.currentSession().then(res => {
    const accessToken = res.getAccessToken();
    jwt = accessToken.getJwtToken();
  });

  return jwt;
};

export const logOff = async () => {
  try {
    await Auth.signOut();
  } catch (error) {
    log.error(error);
  }
};

// -------------------- GRAPHQL --------------------

// -------------- ADMIN
export const getAdmin = async () =>
  await API.graphql(graphqlOperation(queries.getAdmin));

export const newAdmin = async (username, _name, surname, email) =>
  await API.graphql(
    graphqlOperation(mutations.newAdmin, {
      username,
      _name,
      surname,
      email
    })
  );

export const deleteAdmin = async username =>
  await API.graphql(
    graphqlOperation(mutations.deleteAdminUser, {
      username
    })
  );

// -------------- CUSTOMER
export const getCustomers = async () =>
  await API.graphql(graphqlOperation(queries.getCustomers));

export const newCustomer = async (
  crm_id,
  ragione_sociale,
  c_description,
  state,
  account_type,
  timezone,
  webhook_url
) =>
  await API.graphql(
    graphqlOperation(mutations.newCustomer, {
      crm_id,
      ragione_sociale,
      c_description,
      state,
      account_type,
      timezone,
      webhook_url
    })
  );

export const updateCustomer = async (
  crm_id,
  ragione_sociale,
  c_description,
  account_type,
  timezone,
  webhook_url
) =>
  await API.graphql(
    graphqlOperation(mutations.updateCustomer, {
      crm_id,
      ragione_sociale,
      c_description,
      account_type,
      timezone,
      webhook_url
    })
  );

export const deleteCustomer = async crm_id =>
  await API.graphql(
    graphqlOperation(mutations.deleteCustomer, {
      crm_id
    })
  );

export const stateCustomer = async (crm_id, state) =>
  await API.graphql(
    graphqlOperation(mutations.stateCustomer, {
      crm_id,
      state
    })
  );

// ---------------- TECH USER
export const getTechUsersByCustomer = async crm_id =>
  await API.graphql(
    graphqlOperation(queries.getTechUsersByCustomer, { crm_id })
  );

export const newTechUser = async (username, email, policy_id, customer_id) =>
  await API.graphql(
    graphqlOperation(mutations.newTechUser, {
      username,
      email,
      policy_id,
      customer_id
    })
  );

export const updateTechUser = async (username, email, policy_id) =>
  await API.graphql(
    graphqlOperation(mutations.updateTechUser, {
      username,
      email,
      policy_id
    })
  );

export const deleteTechUser = async username =>
  await API.graphql(
    graphqlOperation(mutations.deleteTechUser, {
      username
    })
  );

export const changeTechUserState = async (username, state) =>
  await API.graphql(
    graphqlOperation(mutations.changeTechUserState, {
      username,
      state
    })
  );

export const getPoliciesTU = async () =>
  await API.graphql(graphqlOperation(queries.getPoliciesTU));

// --------------- POLICY
export const getPolicies = async () =>
  await API.graphql(graphqlOperation(queries.getPolicies));

export const newPolicy = async (
  policy_name,
  policy_description,
  quota_metric,
  quota_period,
  quota_limit,
  max_calls_per_day
) =>
  await API.graphql(
    graphqlOperation(mutations.newPolicy, {
      policy_name,
      policy_description,
      quota_metric,
      quota_period,
      quota_limit,
      max_calls_per_day
    })
  );

export const updatePolicy = async (
  policy_id,
  policy_name,
  policy_description,
  quota_metric,
  quota_period,
  quota_limit,
  max_calls_per_day
) =>
  await API.graphql(
    graphqlOperation(mutations.updatePolicy, {
      policy_id,
      policy_name,
      policy_description,
      quota_metric,
      quota_period,
      quota_limit,
      max_calls_per_day
    })
  );

export const deletePolicy = async policy_id =>
  await API.graphql(
    graphqlOperation(mutations.deletePolicy, {
      policy_id
    })
  );

// --------------- WORKSPACE
export const getWorkspacesByPolicy = async policy_id =>
  await API.graphql(
    graphqlOperation(queries.getWorkspacesByPolicy, { policy_id })
  );

export const newWorkspace = async (
  workspace_name,
  n_calls,
  start_time,
  stop_time,
  date_type,
  policy_id
) =>
  await API.graphql(
    graphqlOperation(mutations.newWorkspace, {
      workspace_name,
      n_calls,
      start_time,
      stop_time,
      date_type,
      policy_id
    })
  );

export const updateWorkspace = async (
  workspace_name,
  n_calls,
  start_time,
  stop_time,
  date_type,
  policy_id
) =>
  await API.graphql(
    graphqlOperation(mutations.updateWorkspace, {
      workspace_name,
      n_calls,
      start_time,
      stop_time,
      date_type,
      policy_id
    })
  );

export const deleteWorkspace = async (workspace_name, policy_id) =>
  await API.graphql(
    graphqlOperation(mutations.deleteWorkspace, {
      workspace_name,
      policy_id
    })
  );

export const getDomainsWS = async () =>
  await API.graphql(graphqlOperation(queries.getDomainsWS));

export const getDomainsByWS = async (workspace_name, policy_id) =>
  await API.graphql(
    graphqlOperation(queries.getDomainsByWS, { workspace_name, policy_id })
  );

// --------------- LAYER
export const getDBLayers = async (workspace_name, policy_id) =>
  await API.graphql(
    graphqlOperation(queries.getDBLayers, { workspace_name, policy_id })
  );

export const createLayer = async (
  workspace_name,
  policy_id,
  layer_name,
  tier
) =>
  await API.graphql(
    graphqlOperation(mutations.createLayer, {
      workspace_name,
      policy_id,
      layer_name,
      tier
    })
  );

export const deleteLayer = async (workspace_name, policy_id, layer_name) =>
  await API.graphql(
    graphqlOperation(mutations.deleteLayer, {
      workspace_name,
      policy_id,
      layer_name
    })
  );

// --------------- LAYER CATALOG (global, independent of workspace/policy)
export const getAllLayers = async () =>
  await API.graphql(graphqlOperation(queries.getAllLayers));

export const updateLayerTier = async (layer_name, tier) =>
  await API.graphql(
    graphqlOperation(mutations.updateLayerTier, {
      layer_name,
      tier
    })
  );

// --------------- WS-DOMAIN
export const addDomain = async (workspace_name, policy_id, domain_id) =>
  await API.graphql(
    graphqlOperation(mutations.addDomain, {
      workspace_name,
      policy_id,
      domain_id
    })
  );

export const deleteDomainWS = async (workspace_name, policy_id, domain_id) =>
  await API.graphql(
    graphqlOperation(mutations.deleteDomainWS, {
      workspace_name,
      policy_id,
      domain_id
    })
  );

// --------------- DOMAIN
export const getDomains = async () =>
  await API.graphql(graphqlOperation(queries.getDomains));

export const newDomain = async (
  domain_id,
  domain_name,
  domain_description,
  polygon
) =>
  await API.graphql(
    graphqlOperation(mutations.newDomain, {
      domain_id,
      domain_name,
      domain_description,
      polygon
    })
  );

export const updateDomain = async (
  domain_id,
  domain_name,
  domain_description,
  polygon
) =>
  await API.graphql(
    graphqlOperation(mutations.updateDomain, {
      domain_id,
      domain_name,
      domain_description,
      polygon
    })
  );

export const deleteDomain = async domain_id =>
  await API.graphql(
    graphqlOperation(mutations.deleteDomain, {
      domain_id
    })
  );

// ---------- CHECK

export const checkUserPolicy = async policy_id =>
  await API.graphql(graphqlOperation(queries.checkUserPolicy, { policy_id }));

export const checkUserDomain = async domain_id =>
  await API.graphql(graphqlOperation(queries.checkUserDomain, { domain_id }));

// -------- GROUPS

export const assignGroup = async (username, groupName) =>
  await API.graphql(
    graphqlOperation(mutations.assignGroup, {
      input: {
        username,
        groupName: Array.isArray(groupName) ? groupName : [groupName]
      }
    })
  );

export const getGroup = async username =>
  await API.graphql(
    graphqlOperation(queries.getGroup, {
      input: { username }
    })
  );

// -------- GROUPS / USER-GROUPS (Cascading permissions)
export const getGroups = async () =>
  await API.graphql(graphqlOperation(queries.listGroups));

export const getUserGroups = async username =>
  await API.graphql(graphqlOperation(queries.getUserGroups, { username }));

export const assignUserToGroup = async (username, group_id) =>
  await API.graphql(
    graphqlOperation(mutations.assignUserToGroup, {
      username,
      group_id
    })
  );

export const getAffectedUsersCount = async policy_id =>
  await API.graphql(
    graphqlOperation(queries.getAffectedUsersCount, { policy_id })
  );

export const upsertGroup = async (group_id, policy_id) =>
  await API.graphql(
    graphqlOperation(mutations.upsertGroup, {
      group_id,
      policy_id
    })
  );

// ---------- STATISTICS

export const getCallsOverview = async (year, dateFrom = null, dateTo = null) =>
  await API.graphql(
    graphqlOperation(queries.getCallsOverview, withStatisticsDates(year, dateFrom, dateTo))
  );

export const listTopCallers = async (year, limit = 50, dateFrom = null, dateTo = null) =>
  await API.graphql(
    graphqlOperation(
      queries.listTopCallers,
      withStatisticsDates(year, dateFrom, dateTo, { limit })
    )
  );

export const getCallerBreakdown = async (year, username, dateFrom = null, dateTo = null) =>
  await API.graphql(
    graphqlOperation(
      queries.getCallerBreakdown,
      withStatisticsDates(year, dateFrom, dateTo, { username })
    )
  );

export const listStatisticsUsers = async (year, limit = 500, dateFrom = null, dateTo = null) =>
  await API.graphql(
    graphqlOperation(
      queries.listStatisticsUsers,
      withStatisticsDates(year, dateFrom, dateTo, { limit })
    )
  );

export const listCallsByUser = async (year, username, date_from, date_to) =>
  await API.graphql(
    graphqlOperation(
      queries.listCallsByUser,
      withStatisticsDates(year, date_from, date_to, { username })
    )
  );

export const listServiceStats = async (year, dateFrom = null, dateTo = null) =>
  await API.graphql(
    graphqlOperation(queries.listServiceStats, withStatisticsDates(year, dateFrom, dateTo))
  );

export const listDailyStats = async (year, username = null, dateFrom = null, dateTo = null) => {
  const variables = withStatisticsDates(year, dateFrom, dateTo);

  if (username) {
    variables.username = username;
  }

  return await API.graphql(graphqlOperation(queries.listDailyStats, variables));
};

export const listAvailableYears = async () =>
  await API.graphql(graphqlOperation(queries.listAvailableYears));

// --------------- TIMESERIES CONFIG
export const getTimeseriesConfig = async () =>
  await API.graphql(graphqlOperation(queries.getTimeseriesConfig));

export const saveTimeseriesConfig = async config =>
  await API.graphql(
    graphqlOperation(mutations.saveTimeseriesConfig, { config })
  );
